import { useState } from 'react';
import { PostHelpAssistantFeedback } from '@/apis/helpCenterAPI';
import { RightPointingTailArrowIcon } from '@/assets/icons/icons';
import { useToken } from '@/common/helper';
import type { HelpArticleVote, HelpAssistantMessageProps } from '../../types';
import styles from '../../AppHelpCenter.module.scss';
import HelpVoteButtons from '../HelpVoteButtons/HelpVoteButtons';

const HelpAssistantMessage = ({
  message,
  question,
  insightChatEnabled,
  onOpenArticle,
  onAskInsightChat,
}: HelpAssistantMessageProps) => {
  const { token } = useToken();
  const [vote, setVote] = useState<HelpArticleVote | undefined>();

  if (message.role === 'user') {
    return <div className={styles.assistantUserBubble}>{message.content}</div>;
  }

  const { response } = message;
  // Data questions get the Insight Chat hand-off and related articles instead.
  const dataIntent =
    response.intent === 'data' ? response.data_intent : undefined;
  const links = dataIntent ? dataIntent.related_articles : response.sources;

  // Updates the button right away; a repeat click on the same vote sends nothing.
  const handleVote = (value: HelpArticleVote) => {
    if (value === vote) return;
    const previous = vote;
    setVote(value);
    PostHelpAssistantFeedback(token, {
      value,
      question: question ?? '',
      retrieval_ids: response.retrieval_ids,
      fallback: response.fallback,
    }).catch(() => setVote(previous));
  };

  return (
    <div className={styles.assistantAnswer}>
      {response.fallback_note && (
        <p className={styles.assistantFallbackNote}>{response.fallback_note}</p>
      )}

      <p className={styles.assistantAnswerText}>{response.answer}</p>

      {dataIntent && insightChatEnabled && (
        <button
          type='button'
          className={styles.assistantHandoffButton}
          onClick={() => onAskInsightChat(dataIntent.handoff_question)}
        >
          {dataIntent.handoff_button} &rarr;
        </button>
      )}

      {links.length > 0 && (
        <div className={styles.assistantSources}>
          {dataIntent && (
            <span className={styles.assistantSourcesLabel}>
              {dataIntent.related_label}
            </span>
          )}
          {links.map((source) => (
            <button
              key={source.id}
              type='button'
              className={styles.assistantSourceLink}
              onClick={() => onOpenArticle(source)}
            >
              <RightPointingTailArrowIcon size={14} color='var(--light-blue)' />
              <span>{source.title}</span>
            </button>
          ))}
        </div>
      )}

      {/* PRD: every assistant answer has thumbs up / down. */}
      <HelpVoteButtons
        vote={vote}
        onVote={handleVote}
        className={styles.assistantVote}
      />
    </div>
  );
};

export default HelpAssistantMessage;
